/* eslint-disable no-console */
/**
 * Modal ÚNICO de clasificación contable (SII recibidas) — unifica los dos que había antes
 * (ClasificarFacturaModal "solo esta factura" + ClasificarProveedorModal "todo el proveedor")
 * en un solo componente con un SELECTOR DE ALCANCE visible. Ambos puntos de entrada de
 * ComprasTab llevan acá: el CHIP abre con alcance "solo esta factura"; la FILA, con alcance
 * "todo el proveedor".
 *
 * SOLO consolida UI + wiring: los handlers siguen llamando a las MISMAS funciones puras de
 * `utils/compras/clasificacionProveedor.js` (clasificarFacturaUnica / aplicarReglaProveedor /
 * volverAReglaDelProveedor / desvincularReglaProveedor). No reescribe la lógica ni el schema.
 */
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import {
  MODO,
  esSinClasificar,
  aplicarReglaProveedor,
  clasificarFacturaUnica,
  volverAReglaDelProveedor,
  desvincularReglaProveedor,
  previewDesvinculoRegla,
} from "../../utils/compras/clasificacionProveedor";
import { TIPO_DTE_NOTA_CREDITO } from "../../utils/dte";
import { FECHA_CORTE } from "../../utils/estadoCuenta/saldoInicial";
import { getProveedorPorId } from "../../utils/proveedores";
import { client } from "../../utils/supabase";

export const ALCANCE = { FACTURA: "factura", PROVEEDOR: "proveedor" };

const CUENTAS_POR_GRUPO = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));
const labelCuenta = (v) => CUENTAS_ER.find((c) => c.value === v)?.label || v;
const formatCLP = (n) => `$ ${Number(n || 0).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

const selectStyle = {
  width: "100%",
  height: 36,
  fontSize: "0.82rem",
  padding: "4px 10px",
  border: "1px solid #ced4da",
  borderRadius: 4,
};

const SelectorCuenta = ({ value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
    <option value="">— Seleccionar cuenta —</option>
    {CUENTAS_POR_GRUPO.map(({ grupo, cuentas }) => (
      <optgroup key={grupo} label={grupo}>
        {cuentas.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
);

const Radio = ({ name, value, checked, onChange, children, disabled = false }) => (
  <label style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      style={{ marginRight: 4 }}
    />
    {children}
  </label>
);

const ClasificarModal = ({ doc, alcanceInicial = ALCANCE.FACTURA, onClose, onAplicada }) => {
  const {
    id: facturaId,
    folio,
    proveedorId,
    proveedorNombre,
    pais,
    cuentaEr,
    cuentaEr2,
    montoEr2,
    neto,
    exento,
    tipoContable,
    tipoDte,
    clasificacionManual,
    razonSocial,
  } = doc;

  // Una nota de crédito (tipo_dte=61) NUNCA puede clasificarse como Activo: no es un bien de capital,
  // resta del gasto. Se bloquea el radio Activo y el guardado (regla 10.177).
  const esNotaCredito = Number(tipoDte) === TIPO_DTE_NOTA_CREDITO;

  // Base sin IVA que se reparte en el carve-out: neto si es afecta, exento si no (espejo de
  // `importeSinIVA` en costosER.js). Sobre esta base se valida el saldo positivo.
  const baseCarveOut = Number(neto) > 0 ? Number(neto) : Number(exento) || 0;

  // Sin proveedor identificado, el único alcance posible es la factura.
  const [alcance, setAlcance] = useState(proveedorId ? alcanceInicial : ALCANCE.FACTURA);
  // Naturaleza. Prefill desde la clasificación vigente de la factura.
  const [modo, setModo] = useState(tipoContable === "activo" ? MODO.ACTIVO : MODO.GASTO);
  const [cuentaER, setCuentaER] = useState(tipoContable === "activo" ? "" : cuentaEr || "");
  // Sub-elección del modo híbrido (solo alcance proveedor): cómo se clasifica ESTA factura.
  const [hibTipo, setHibTipo] = useState(MODO.GASTO);
  const [hibCuenta, setHibCuenta] = useState(cuentaEr || "");
  // Carve-out (solo alcance factura + gasto): reparte parte del neto a una 2ª cuenta del ER.
  const [carveCuenta, setCarveCuenta] = useState(cuentaEr2 || "");
  const [carveMonto, setCarveMonto] = useState(montoEr2 != null ? String(montoEr2) : "");
  const [prov, setProv] = useState(null);
  const [nSinClasificar, setNSinClasificar] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // Diálogo de confirmación del desvínculo (acción en masa): { revertidas, manuales } o null.
  const [desvinculoPreview, setDesvinculoPreview] = useState(null);

  useEffect(() => {
    if (!proveedorId) return undefined;
    let cancelado = false;
    getProveedorPorId(proveedorId)
      .then((p) => {
        if (cancelado || !p) return;
        setProv(p);
        // Prefill de la cuenta del híbrido desde el default viejo del proveedor, si existe.
        if (p.cuenta_er_default) setHibCuenta((prev) => prev || p.cuenta_er_default);
      })
      .catch((err) => console.error("ClasificarModal: error cargando proveedor", err));
    // N real = recibidas 2026 del proveedor sin clasificar (mismo SELECT + predicado puro
    // esSinClasificar que usa aplicarReglaProveedor). Solo para mostrar el conteo en el radio.
    client
      .from("facturas_sii_recibidas")
      .select("id, tipo_dte, fecha_emision, tipo_contable, cuenta_er, clasificacion_manual")
      .eq("pais", pais)
      .eq("proveedor_id", proveedorId)
      .gte("fecha_emision", FECHA_CORTE)
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error) {
          console.error("ClasificarModal: error contando sin clasificar", error);
          setNSinClasificar(null);
        } else {
          setNSinClasificar((data || []).filter(esSinClasificar).length);
        }
      });
    return () => {
      cancelado = true;
    };
  }, [proveedorId, pais]);

  const reglaActual = prov?.clasificacion_modo;

  const cambiarAlcance = (a) => {
    setAlcance(a);
    // Híbrido no aplica a una sola factura: al pasar a alcance factura se cae a Gasto.
    if (a === ALCANCE.FACTURA && modo === MODO.HIBRIDO) setModo(MODO.GASTO);
  };

  // Carve-out solo en alcance factura + gasto. Se considera "activo" si el usuario tocó cuenta
  // o monto (parcial → hay que completarlo o dejarlo vacío del todo).
  const carveActivo = alcance === ALCANCE.FACTURA && modo === MODO.GASTO && (Boolean(carveCuenta) || carveMonto !== "");
  const carveMontoNum = Number(carveMonto);
  const carveSaldoPrincipal = baseCarveOut - carveMontoNum;

  // Feedback en vivo del carve-out (mensaje de error o null). Se calcula fuera del JSX y con
  // early-returns para no anidar ternarios (choca con la regla `indent` de eslint).
  const calcCarveError = () => {
    if (carveMontoNum >= baseCarveOut) return `El monto debe ser menor al neto (${formatCLP(baseCarveOut)}).`;
    if (!carveCuenta) return "Elegí la cuenta secundaria del reparto.";
    if (carveCuenta === cuentaER) return "La cuenta secundaria debe ser distinta de la principal.";
    return null;
  };
  const carveError = calcCarveError();
  const carvePreview = `Principal (${labelCuenta(cuentaER)}): ${formatCLP(
    carveSaldoPrincipal
  )} · Secundaria (${labelCuenta(carveCuenta)}): ${formatCLP(carveMontoNum)}`;

  const aplicar = async () => {
    // Guard: una NC nunca puede quedar como Activo (ni por modo Activo directo, ni por el sub-modo
    // Activo del híbrido). Bloqueo explícito con mensaje claro (regla 10.177).
    if (esNotaCredito && (modo === MODO.ACTIVO || (modo === MODO.HIBRIDO && hibTipo === MODO.ACTIVO))) {
      return toast.error("Una nota de crédito no puede clasificarse como Activo. Elegí Gasto.");
    }
    if (alcance === ALCANCE.PROVEEDOR && modo === MODO.HIBRIDO) {
      if (hibTipo === MODO.GASTO && !hibCuenta) return toast.error("Elegí la cuenta para esta factura");
    } else if (modo === MODO.GASTO && !cuentaER) {
      return toast.error("Elegí una cuenta del ER");
    }

    // Validación del carve-out (reparto a 2ª cuenta): ambos campos, monto > 0, saldo positivo
    // en la principal y cuentas distintas.
    let carveOut = null;
    if (carveActivo) {
      if (!carveCuenta) return toast.error("Elegí la cuenta secundaria del reparto");
      if (!(carveMontoNum > 0)) return toast.error("El monto del reparto debe ser mayor a 0");
      if (carveMontoNum >= baseCarveOut)
        return toast.error(`El monto del reparto debe ser menor al neto (${formatCLP(baseCarveOut)})`);
      if (carveCuenta === cuentaER) return toast.error("La cuenta secundaria debe ser distinta de la principal");
      carveOut = { cuentaEr2: carveCuenta, montoEr2: carveMontoNum };
    }

    setGuardando(true);
    try {
      if (alcance === ALCANCE.FACTURA) {
        // Excepción per-factura (clasificacion_manual = true): ningún automatismo la pisa.
        // carveOut null limpia cualquier reparto anterior (ver construirCarveOut).
        await clasificarFacturaUnica({ facturaId, modo, cuentaEr: modo === MODO.GASTO ? cuentaER : null, carveOut });
        toast.success("Excepción aplicada a esta factura");
      } else if (modo === MODO.HIBRIDO) {
        // Marca el proveedor híbrido (no propaga) y clasifica solo la factura abierta.
        await aplicarReglaProveedor({ proveedorId, pais, modo: MODO.HIBRIDO });
        await clasificarFacturaUnica({ facturaId, modo: hibTipo, cuentaEr: hibTipo === MODO.GASTO ? hibCuenta : null });
        toast.success("Proveedor marcado como híbrido · esta factura clasificada");
      } else {
        const { propagadas, candidatas } = await aplicarReglaProveedor({ proveedorId, pais, modo, cuentaEr: cuentaER });
        if (candidatas === 0) {
          toast.success("Regla guardada (no había facturas sin clasificar)");
        } else if (propagadas === candidatas) {
          toast.success(
            `Regla aplicada · ${propagadas} factura${propagadas === 1 ? "" : "s"} clasificada${propagadas === 1 ? "" : "s"}`
          );
        } else {
          // propagadas < candidatas → algunas candidatas NO se escribieron (regla 10.177): avisar,
          // no fingir éxito total.
          toast.error(
            `Regla guardada, pero solo se clasificaron ${propagadas} de ${candidatas} facturas (${candidatas - propagadas} no se pudieron escribir)`
          );
        }
      }
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarModal: error al aplicar", err);
      toast.error("No se pudo aplicar la clasificación");
    } finally {
      setGuardando(false);
    }
  };

  const volverALaRegla = async () => {
    setGuardando(true);
    try {
      await volverAReglaDelProveedor({ facturaId, proveedorId });
      toast.success(
        proveedorId ? "Factura devuelta a la regla del proveedor" : "Excepción quitada (queda sin clasificar)"
      );
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarModal: error al volver a la regla", err);
      toast.error("No se pudo quitar la excepción");
    } finally {
      setGuardando(false);
    }
  };

  // Desvincular regla del proveedor (O-3): dos pasos con confirmación.
  const pedirDesvincular = async () => {
    setGuardando(true);
    try {
      setDesvinculoPreview(await previewDesvinculoRegla({ proveedorId, pais }));
    } catch (err) {
      console.error("ClasificarModal: error al calcular el desvínculo", err);
      toast.error("No se pudo calcular el desvínculo");
    } finally {
      setGuardando(false);
    }
  };

  const confirmarDesvincular = async () => {
    setGuardando(true);
    try {
      const { revertidas, manuales } = await desvincularReglaProveedor({ proveedorId, pais });
      toast.success(
        `Regla eliminada · ${revertidas} factura${revertidas === 1 ? "" : "s"} a sin clasificar` +
          (manuales > 0
            ? ` · ${manuales} excepción${manuales === 1 ? "" : "es"} conservada${manuales === 1 ? "" : "s"}`
            : "")
      );
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarModal: error al desvincular", err);
      toast.error("No se pudo eliminar la regla");
    } finally {
      setGuardando(false);
    }
  };

  const esProveedor = alcance === ALCANCE.PROVEEDOR;
  const nLabel = nSinClasificar == null ? "…" : nSinClasificar;

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>
        Clasificar — Folio {folio} &middot; {proveedorNombre}
      </ModalHeader>

      {desvinculoPreview ? (
        <>
          <ModalBody>
            <div style={{ fontSize: "0.85rem", lineHeight: 1.6 }}>
              Vas a desvincular la regla de <strong>{proveedorNombre}</strong>. Se devolverán{" "}
              <strong>{desvinculoPreview.revertidas}</strong> factura{desvinculoPreview.revertidas === 1 ? "" : "s"} a{" "}
              <strong>sin clasificar</strong>.
              {desvinculoPreview.manuales > 0 && (
                <>
                  {" "}
                  Se conservan <strong>{desvinculoPreview.manuales}</strong> excepción
                  {desvinculoPreview.manuales === 1 ? "" : "es"} manual{desvinculoPreview.manuales === 1 ? "" : "es"}.
                </>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              size="sm"
              onClick={() => setDesvinculoPreview(null)}
              disabled={guardando}
              style={{ marginRight: 8 }}
            >
              Cancelar
            </Button>
            <Button
              color="danger"
              size="sm"
              style={{ minWidth: 110 }}
              onClick={confirmarDesvincular}
              disabled={guardando}
            >
              {guardando ? "Desvinculando…" : "Confirmar"}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            {/* Alcance — el selector que faltaba: mismo modal, dos alcances explícitos. */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 6 }}>Alcance</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "0.82rem" }}>
                <Radio
                  name="alcance"
                  value={ALCANCE.FACTURA}
                  checked={alcance === ALCANCE.FACTURA}
                  onChange={() => cambiarAlcance(ALCANCE.FACTURA)}
                >
                  Solo esta factura
                </Radio>
                <Radio
                  name="alcance"
                  value={ALCANCE.PROVEEDOR}
                  checked={alcance === ALCANCE.PROVEEDOR}
                  onChange={() => cambiarAlcance(ALCANCE.PROVEEDOR)}
                  disabled={!proveedorId}
                >
                  {proveedorId ? (
                    <>
                      Todo el proveedor <strong>{proveedorNombre}</strong> ({nLabel} sin clasificar)
                    </>
                  ) : (
                    <span style={{ color: "#adb5bd" }}>Todo el proveedor (sin proveedor identificado)</span>
                  )}
                </Radio>
              </div>
            </div>

            {reglaActual && esProveedor && (
              <div
                style={{
                  background: "#eafaf1",
                  border: "1px solid #abebc6",
                  color: "#1e8449",
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: "0.78rem",
                  marginBottom: 12,
                }}
              >
                Regla actual:{" "}
                <strong>
                  {reglaActual === MODO.GASTO && `Gasto → ${labelCuenta(prov.cuenta_er_default)}`}
                  {reglaActual === MODO.ACTIVO && "Activo"}
                  {reglaActual === MODO.HIBRIDO && "Híbrido (se clasifica factura por factura)"}
                </strong>
                . Cambiala abajo (re-propaga a las que sigan sin clasificar) o desvinculala.
              </div>
            )}

            {/* Texto explicativo dinámico según alcance. */}
            <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 12 }}>
              {esProveedor ? (
                <>
                  Se aplica a <strong>todas</strong> las facturas SII recibidas 2026 de este proveedor sin clasificar.
                  Nunca pisa una clasificación previa. Las futuras entran auto-clasificadas.
                </>
              ) : (
                <>
                  Vale <strong>solo para esta factura</strong>. No pisa la regla del proveedor.
                </>
              )}
            </div>

            {/* Naturaleza. Híbrido solo cuando el alcance es el proveedor. */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                {esProveedor ? "Naturaleza del proveedor" : "Clasificación de esta factura"}
              </label>
              <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", flexWrap: "wrap" }}>
                <Radio
                  name="modo"
                  value={MODO.GASTO}
                  checked={modo === MODO.GASTO}
                  onChange={() => setModo(MODO.GASTO)}
                >
                  Gasto
                </Radio>
                <Radio
                  name="modo"
                  value={MODO.ACTIVO}
                  checked={modo === MODO.ACTIVO}
                  onChange={() => setModo(MODO.ACTIVO)}
                  disabled={esNotaCredito}
                >
                  Activo{esNotaCredito ? " (no aplica a NC)" : ""}
                </Radio>
                {esProveedor && (
                  <Radio
                    name="modo"
                    value={MODO.HIBRIDO}
                    checked={modo === MODO.HIBRIDO}
                    onChange={() => setModo(MODO.HIBRIDO)}
                  >
                    Híbrido (mezcla)
                  </Radio>
                )}
              </div>
            </div>

            {modo === MODO.GASTO && (
              <div style={{ marginBottom: 4 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Cuenta del Estado de Resultados
                </label>
                <SelectorCuenta value={cuentaER} onChange={setCuentaER} />
              </div>
            )}

            {/* Carve-out: parte del neto va a una 2ª cuenta (solo esta factura + gasto). El saldo
                queda en la cuenta principal. Caso Entel: internet ($) → servicios_internet, resto
                → telefonía. Opcional: si se deja vacío, no hay reparto. */}
            {alcance === ALCANCE.FACTURA && modo === MODO.GASTO && cuentaER && (
              <div style={{ border: "1px dashed #ccc", borderRadius: 6, padding: "10px 12px", marginTop: 12 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Parte de esta factura va a otra cuenta <span style={{ color: "#909497" }}>(opcional)</span>
                </label>
                <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 8 }}>
                  Se resta de <strong>{labelCuenta(cuentaER)}</strong> y se manda a la cuenta que elijas. El neto de
                  esta factura es <strong>{formatCLP(baseCarveOut)}</strong>.
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <SelectorCuenta value={carveCuenta} onChange={setCarveCuenta} />
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="Monto"
                    value={carveMonto}
                    onChange={(e) => setCarveMonto(e.target.value)}
                    style={{ ...selectStyle, width: 130 }}
                  />
                </div>
                {carveActivo && carveMontoNum > 0 && (
                  <div style={{ fontSize: "0.78rem", color: carveError ? "#c0392b" : "#1e8449" }}>
                    {carveError || carvePreview}
                  </div>
                )}
              </div>
            )}

            {modo === MODO.ACTIVO && (
              <div
                style={{
                  background: "#eaf2f8",
                  color: "#2874a6",
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: "0.78rem",
                }}
              >
                {esProveedor
                  ? "Sus recibidas 2026 sin clasificar se marcan activo y salen de la línea de costo del ER."
                  : "Se marca como activo y sale de la línea de costo del ER."}{" "}
                El alta / depreciación del activo se hace aparte.
              </div>
            )}

            {esProveedor && modo === MODO.HIBRIDO && (
              <div style={{ border: "1px dashed #ccc", borderRadius: 6, padding: "10px 12px" }}>
                <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 8 }}>
                  El proveedor mezcla gasto y activo: no se propaga. Se clasifica <strong>solo esta factura</strong> y
                  sus futuras facturas no se auto-asignan.
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", marginBottom: 8 }}>
                  <Radio
                    name="hibTipo"
                    value={MODO.GASTO}
                    checked={hibTipo === MODO.GASTO}
                    onChange={() => setHibTipo(MODO.GASTO)}
                  >
                    Esta = Gasto
                  </Radio>
                  <Radio
                    name="hibTipo"
                    value={MODO.ACTIVO}
                    checked={hibTipo === MODO.ACTIVO}
                    onChange={() => setHibTipo(MODO.ACTIVO)}
                  >
                    Esta = Activo
                  </Radio>
                </div>
                {hibTipo === MODO.GASTO && <SelectorCuenta value={hibCuenta} onChange={setHibCuenta} />}
              </div>
            )}
          </ModalBody>

          <ModalFooter style={{ justifyContent: "space-between" }}>
            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
              {reglaActual && (
                <Button
                  color="link"
                  size="sm"
                  style={{ color: "#c0392b", padding: 0 }}
                  onClick={pedirDesvincular}
                  disabled={guardando}
                >
                  Desvincular regla
                </Button>
              )}
              {alcance === ALCANCE.FACTURA && clasificacionManual && (
                <Button
                  color="link"
                  size="sm"
                  style={{ color: "#c0392b", padding: 0 }}
                  onClick={volverALaRegla}
                  disabled={guardando}
                >
                  Volver a la regla del proveedor
                </Button>
              )}
            </span>
            <span>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={onClose}
                disabled={guardando}
                style={{ marginRight: 8 }}
              >
                Cancelar
              </Button>
              <Button color="success" size="sm" style={{ minWidth: 120 }} onClick={aplicar} disabled={guardando}>
                {guardando ? "Guardando…" : esProveedor ? "Aplicar" : "Aplicar excepción"}
              </Button>
            </span>
          </ModalFooter>
        </>
      )}

      {razonSocial && razonSocial !== proveedorNombre && !desvinculoPreview && (
        <div style={{ color: "#909497", fontSize: "0.72rem", padding: "0 16px 12px" }}>
          Razón social SII: {razonSocial}
        </div>
      )}
    </Modal>
  );
};

export default ClasificarModal;
